import React from 'react';
import get from 'lodash/get';
import type { FormErrors } from '@mantine/form';

import { ApiError, NetworkError } from '../hooks/use-api';
import { HEADER_HEIGHT, PROJECT_SLUG } from '../constants';

export function scrollToFirstError() {
  const errorSelectors = [
    'form [aria-invalid="true"]', // TextInput, Select, Textarea etc.
    'form [error]', // SegmentedControl
    `form .${PROJECT_SLUG}-CheckboxGroup-error`, // CheckboxGroup
    `form .${PROJECT_SLUG}-InputWrapper-error`, // InputWrapper
  ];

  setTimeout(() => {
    const firstError = Array.from(document.querySelectorAll(errorSelectors.join(',')))
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
    if (firstError) {
      const firstErrorPosition = firstError.getBoundingClientRect().top;
      window.scrollTo({
        top: firstErrorPosition + window.pageYOffset - HEADER_HEIGHT - 100,
        behavior: 'smooth',
      });
    }
  }, 100);
}

export function scrollToAlert() {
  setTimeout(() => {
    const alertEl = document.querySelectorAll('[role="alert"]')[0];
    if (alertEl) {
      const alertElPosition = alertEl.getBoundingClientRect().top;
      window.scrollTo({
        top: alertElPosition + window.pageYOffset - HEADER_HEIGHT - 50,
        behavior: 'smooth',
      });
    }
  }, 100);
}

export interface TabFieldMapping {
  [key: string]: string;
}

export function handleFormErrors(
  error: unknown,
  formErrorSetter: (errors: FormErrors) => void,
  errorMessageSetter: React.Dispatch<React.SetStateAction<string | null>>,
  options?: {
    hiddenFields?: string[];
    tabFieldMapping?: TabFieldMapping;
    onTabChange?: (tab: string) => void;
  },
): void {
  const { hiddenFields = [], tabFieldMapping, onTabChange } = options || {};

  if ((error instanceof ApiError || error instanceof NetworkError) && error.isHandled) return;

  if (error instanceof ApiError && error.hasValidationErrors()) {
    formErrorSetter(error.validationErrors);
    scrollToFirstError();

    // Handle tab switching if mapping is provided
    if (tabFieldMapping && onTabChange) {
      const firstErrorField = Object.keys(error.validationErrors)[0];
      const baseFieldName = firstErrorField.split('.')[0];
      const targetTab = tabFieldMapping[baseFieldName] || Object.values(tabFieldMapping)[0];

      if (targetTab) {
        onTabChange(targetTab);
        setTimeout(() => {
          scrollToFirstError();
        }, 100);
      }
    }

    // Handle hidden field errors
    if (hiddenFields.length > 0) {
      let isHiddenFieldError = false;
      hiddenFields.forEach((field) => {
        const validationError = error.validationErrors[field];
        if (validationError) {
          errorMessageSetter(String(validationError));
          isHiddenFieldError = true;
        }
      });

      if (isHiddenFieldError) scrollToAlert();
    }

    return;
  }

  errorMessageSetter((error as Error).message);
  scrollToAlert();
}

export function handleNonFormErrors(
  error: unknown,
  errorCallback: (message: string) => void,
): void {
  if ((error instanceof ApiError || error instanceof NetworkError) && error.isHandled) return;

  let errorMessage = get(error, 'message', 'Unknown error');

  if (error instanceof ApiError && error.hasValidationErrors()) {
    errorMessage = 'Validation error';

    const firstError = Object.values(error.validationErrors)[0];
    if (firstError !== undefined) {
      if (typeof firstError === 'string') {
        errorMessage = firstError;
      }

      if (Array.isArray(firstError)) {
        errorMessage = String(firstError[0]);
      }
    }
  }

  errorCallback(errorMessage);
}

export function handleFormClientValidationErrors() {
  scrollToFirstError();
}

export function handleFormClientValidationErrorsWithTabChange(
  validationErrors: FormErrors,
  options: {
    tabFieldMapping: TabFieldMapping;
    onTabChange: (tab: string) => void;
  },
): void {
  const { tabFieldMapping, onTabChange } = options;
  const firstErrorField = Object.keys(validationErrors)[0];
  const baseFieldName = firstErrorField.split('.')[0];
  const targetTab = tabFieldMapping[baseFieldName] || Object.values(tabFieldMapping)[0];

  if (targetTab) {
    onTabChange(targetTab);
    setTimeout(() => {
      scrollToFirstError();
    }, 100);
  }
}
